import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { WS_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';
import type { ZoomOverviewData } from '@/components/report/overview-types';
import type { InteractiveMode } from '@/types';
import { InteractiveOptions } from '@/components/chat/InteractiveOptions';

// =============================================================================
// Types
// =============================================================================

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  interactive?: InteractiveMode;
}

interface ReportChatProps {
  data: ZoomOverviewData;
  accountId?: string;
  width?: number;
  onWidthChange?: (width: number) => void;
  onClose?: () => void;
}

const MIN_WIDTH = 320;
const MAX_WIDTH = 720;

// =============================================================================
// Suggested Questions
// =============================================================================

const SUGGESTED_QUESTIONS = [
  'What security risks do you see?',
  'How does usage compare to the plan?',
  'What optimizations do you recommend?',
  'Summarize this account for leadership',
  'Lock the waiting room setting for all users',
];

const GREETING =
  "I can help you analyze this account's data. Ask me about security, usage patterns, or recommendations.";

// =============================================================================
// Data Compaction — keep URL under 8KB
// =============================================================================

/**
 * Compact overview data for the WebSocket handshake.
 * Mirrors backend's compactAccountData() — strips individual records,
 * keeps aggregates and top items. Keeps URL under ~8KB.
 */
function compactForChat(data: ZoomOverviewData): string {
  const compact: Record<string, unknown> = {
    fetchedAt: data.fetchedAt,
    scope: data.scope,
    account: data.account,
    plans: data.plans,
    settings: data.settings,
  };

  // Users: keep totals only
  const users = data.users as unknown as Record<string, unknown> | undefined;
  if (users && !('_status' in users)) {
    compact.users = {
      totalRecords: users.totalRecords,
      byType: users.byType,
      byStatus: users.byStatus,
    };
  } else {
    compact.users = users;
  }

  // Usage: keep totals + top 5
  const usage = data.usage as unknown as Record<string, unknown> | undefined;
  if (usage && !('_status' in usage)) {
    compact.usage = {
      totalMeetings: usage.totalMeetings,
      totalParticipants: usage.totalParticipants,
      totalMeetingMinutes: usage.totalMeetingMinutes,
      topMeetings: Array.isArray(usage.topMeetings) ? usage.topMeetings.slice(0, 5) : undefined,
    };
  } else {
    compact.usage = usage;
  }

  // Recordings: totals only
  const recordings = data.recordings as unknown as Record<string, unknown> | undefined;
  compact.recordings = {
    totalRecords: recordings?.totalRecords,
    totalSize: recordings?.totalSize,
  };

  compact.phone = data.phone;
  compact.rooms = data.rooms;
  compact.roles = data.roles;
  compact.groups = data.groups;
  compact.marketplace = data.marketplace;
  compact.archive = data.archive;
  compact.meetingQuality = data.meetingQuality;
  compact.satisfaction = data.satisfaction;
  compact.liveMeetings = (data as unknown as Record<string, unknown>).liveMeetings;

  // AI Summary: scores only
  const aiSummary = data.aiSummary as unknown as Record<string, unknown> | undefined;
  if (aiSummary) {
    compact.aiSummary = {
      scores: aiSummary.scores,
      keyPoints: aiSummary.keyPoints,
      suggestions: aiSummary.suggestions,
    };
  }

  return JSON.stringify(compact);
}

// =============================================================================
// Component
// =============================================================================

export function ReportChat({ data, accountId, width, onWidthChange, onClose }: ReportChatProps) {
  const [messages, setMessages] = useState<Message[]>([
    { id: '1', role: 'assistant', content: GREETING },
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [thinkingMessage, setThinkingMessage] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [planningSessionId, setPlanningSessionId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Drag-to-resize handler
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const currentWidth = width ?? MIN_WIDTH;
      dragRef.current = { startX: e.clientX, startWidth: currentWidth };
      setIsDragging(true);

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const delta = dragRef.current.startX - ev.clientX; // dragging left = positive delta = wider
        const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragRef.current.startWidth + delta));
        onWidthChange?.(newWidth);
      };

      const onMouseUp = () => {
        dragRef.current = null;
        setIsDragging(false);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width, onWidthChange],
  );

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, thinkingMessage]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  const sendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || isStreaming) return;

      const token = getToken();
      if (!token) return;

      // Add user message
      const userMsg: Message = { id: Date.now().toString(), role: 'user', content };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setIsStreaming(true);
      setStreamingContent('');
      setThinkingMessage('');
      setShowSuggestions(false);

      // Build history from existing messages (skip the greeting, exclude current message — it's sent separately)
      const history = messages
        .filter((m) => m.id !== '1')
        .map((m) => ({ role: m.role, content: m.content }));

      // Build WebSocket URL with handshake params
      const params = new URLSearchParams();
      params.set('token', token);
      if (accountId) params.set('accountId', accountId);
      params.set('accountData', compactForChat(data));
      params.set('history', JSON.stringify(history));
      params.set('message', content);
      if (planningSessionId) params.set('planningSessionId', planningSessionId);

      const wsUrl = `${WS_BASE}/report/chat?${params.toString()}`;

      let accumulated = '';
      let pendingInteractive: InteractiveMode | null = null;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);

            switch (msg.type) {
              case 'token':
                if (msg.content) {
                  accumulated += msg.content;
                  setStreamingContent(accumulated);
                  setThinkingMessage('');
                }
                break;

              case 'thinking':
                if (msg.content) {
                  setThinkingMessage(msg.content);
                }
                break;

              case 'interactive':
                pendingInteractive = msg.interactive;
                if (msg.interactive?.sessionId) {
                  setPlanningSessionId(msg.interactive.sessionId);
                }
                break;

              case 'done':
                // Finalize: add accumulated content as assistant message
                if (accumulated || pendingInteractive) {
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: (Date.now() + 1).toString(),
                      role: 'assistant',
                      content: accumulated,
                      interactive: pendingInteractive || undefined,
                    },
                  ]);
                }
                // Clear planning session on done without interactive (approval completed)
                if (!pendingInteractive) setPlanningSessionId(null);
                setStreamingContent('');
                setThinkingMessage('');
                setIsStreaming(false);
                ws.close();
                wsRef.current = null;
                break;

              case 'error':
                // Show error inline as an assistant message
                setMessages((prev) => [
                  ...prev,
                  {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    content: `Something went wrong: ${msg.message || 'Unknown error'}. Please try again.`,
                  },
                ]);
                setStreamingContent('');
                setThinkingMessage('');
                setIsStreaming(false);
                ws.close();
                wsRef.current = null;
                break;
            }
          } catch {
            // Ignore parse errors
          }
        };

        ws.onerror = () => {
          setMessages((prev) => [
            ...prev,
            {
              id: (Date.now() + 1).toString(),
              role: 'assistant',
              content: 'Connection error. Please try again.',
            },
          ]);
          setStreamingContent('');
          setThinkingMessage('');
          setIsStreaming(false);
          wsRef.current = null;
        };

        ws.onclose = () => {
          // If we're still streaming when socket closes unexpectedly, finalize
          if (accumulated && wsRef.current === ws) {
            setMessages((prev) => [
              ...prev,
              {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: accumulated,
              },
            ]);
            setStreamingContent('');
            setThinkingMessage('');
            setIsStreaming(false);
          }
          wsRef.current = null;
        };
      } catch {
        setIsStreaming(false);
      }
    },
    [data, accountId, isStreaming, messages, planningSessionId],
  );

  // Listen for OAuth popup completion and auto-verify scopes
  useEffect(() => {
    function handleOAuthMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'zoom-oauth-complete') return;
      if (event.data.status === 'success') {
        sendMessage('refresh_scopes');
      }
    }
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [sendMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input.trim());
  };

  return (
    <aside
      className={`bg-card border-l border-border flex flex-col h-full relative ${
        (width ?? MIN_WIDTH) > MIN_WIDTH ? 'shadow-xl' : ''
      }`}
    >
      {/* Drag handle */}
      <div
        onMouseDown={handleDragStart}
        className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10 transition-colors hover:bg-primary/40 ${
          isDragging ? 'bg-primary/40' : ''
        }`}
      />

      {/* Header */}
      <div className="h-14 flex items-center px-4 border-b border-border shrink-0">
        <span className="flex-1 text-sm font-medium text-foreground">Chat with Zac</span>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            aria-label="Close chat"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                message.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-foreground'
              }`}
            >
              {message.role === 'assistant' ? (
                <>
                  {message.content && (
                    <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    </div>
                  )}
                  {message.interactive && (
                    <InteractiveOptions
                      interactive={message.interactive}
                      onSelect={(value) => sendMessage(value)}
                      disabled={isStreaming}
                    />
                  )}
                </>
              ) : (
                message.content
              )}
            </div>
          </div>
        ))}

        {/* Streaming response */}
        {isStreaming && streamingContent && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-secondary text-foreground">
              <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingContent}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Thinking indicator */}
        {isStreaming && thinkingMessage && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-muted-foreground bg-secondary/50">
              <Loader2 className="h-3 w-3 animate-spin" />
              {thinkingMessage}
            </div>
          </div>
        )}

        {/* Loading spinner (no content yet) */}
        {isStreaming && !streamingContent && !thinkingMessage && (
          <div className="flex justify-start">
            <div className="bg-secondary rounded-lg px-3 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}

        {/* Suggested Questions */}
        {showSuggestions && !isStreaming && (
          <div className="space-y-2">
            {SUGGESTED_QUESTIONS.map((question, index) => (
              <button
                key={index}
                onClick={() => sendMessage(question)}
                className="w-full text-left px-3 py-2 text-sm text-muted-foreground bg-secondary/50 hover:bg-secondary hover:text-foreground rounded-lg transition-colors"
              >
                {question}
              </button>
            ))}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-border shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Zac anything..."
            disabled={isStreaming}
            className="flex-1 bg-secondary border-0 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!input.trim() || isStreaming}
            className="p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>
    </aside>
  );
}
